import {
    BriefcaseIcon,
    BuildingOffice2Icon,
    CalendarDaysIcon,
    ClockIcon,
    DocumentTextIcon,
    IdentificationIcon,
    MagnifyingGlassIcon,
    UserGroupIcon,
    UserIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { ActionIcon, Box, Button, Group, MultiSelect, rem, Stack, Switch, Text, TextInput, Tooltip } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useDebouncedValue } from "@mantine/hooks";
import { SVGProps, useEffect, useState } from "react";
import { FilterPill } from "../../../components/common/FilterPill";
import { useSearchConfig } from "../../../context/SearchConfigContext";
import { useTranslation } from "../../../hooks/useTranslation";
import { dateInputStyles, getDateInputProps } from "../../../styles/dateInputStyles";
import { toLocalDateString } from "../../../utils/dateFormat";
import { useEmployeeFilterDictionaries } from "../hooks/useEmployeeFilterDictionaries";
import { useEmployeeOverviewFilters } from "../hooks/useEmployeeOverviewFilters";

const IconMagnifyingGlass = (props: SVGProps<SVGSVGElement>) => <MagnifyingGlassIcon {...props} />;
const IconXMark = (props: SVGProps<SVGSVGElement>) => <XMarkIcon {...props} />;
const IconBuilding = (props: SVGProps<SVGSVGElement>) => <BuildingOffice2Icon {...props} />;
const IconBriefcase = (props: SVGProps<SVGSVGElement>) => <BriefcaseIcon {...props} />;
const IconClock = (props: SVGProps<SVGSVGElement>) => <ClockIcon {...props} />;
const IconIdentification = (props: SVGProps<SVGSVGElement>) => <IdentificationIcon {...props} />;
const IconUserGroup = (props: SVGProps<SVGSVGElement>) => <UserGroupIcon {...props} />;
const IconCalendarDays = (props: SVGProps<SVGSVGElement>) => <CalendarDaysIcon {...props} />;
const IconUser = (props: SVGProps<SVGSVGElement>) => <UserIcon {...props} />;
const IconDocumentText = (props: SVGProps<SVGSVGElement>) => <DocumentTextIcon {...props} />;

const pillIconColor = (active: boolean) =>
    active ? "var(--mantine-color-brand-5)" : "var(--mantine-color-dimmed)";

const toIsoDate = (date: Date | null): string | undefined =>
    date ? toLocalDateString(date) : undefined;

const fromIsoDate = (value: string | null): Date | null => (value ? new Date(value) : null);

interface EmployeeOverviewSearchProps {
    filters: ReturnType<typeof useEmployeeOverviewFilters>;
}

export function EmployeeOverviewSearch({ filters }: EmployeeOverviewSearchProps) {
    const { config } = useSearchConfig();
    const { t } = useTranslation();
    const dictionaries = useEmployeeFilterDictionaries();
    const dateInputProps = getDateInputProps();

    const [inputValue, setInputValue] = useState(filters.searchQuery);
    const [debouncedInput] = useDebouncedValue(inputValue, config.debounceDelay);

    useEffect(() => {
        filters.setSearchQuery(debouncedInput);
        filters.setPageIndex(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedInput]);

    useEffect(() => {
        if (filters.searchQuery === "") setInputValue("");
    }, [filters.searchQuery]);

    const handleClear = () => {
        setInputValue("");
        filters.setSearchQuery("");
        filters.setPageIndex(0);
    };

    const toOptions = (entries: { id: number; displayName: string }[] | undefined) =>
        (entries ?? []).map((entry) => ({ value: entry.id.toString(), label: entry.displayName }));

    const idsToStrings = (ids: number[]) => ids.map((id) => id.toString());
    const stringsToIds = (values: string[]) => values.map((value) => parseInt(value, 10));

    return (
        <Stack gap="xs" mb="sm">
            {/* ── Row 1: name search ── */}
            <Group align="center" gap="sm" wrap="nowrap">
                <Box style={{ flex: 1 }}>
                    <TextInput
                        size="md"
                        placeholder={t("EMPLOYEEOVERVIEW_SEARCH_PLACEHOLDER", undefined, "Find by first or last name…")}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.currentTarget.value)}
                        leftSection={<IconMagnifyingGlass width={20} />}
                        aria-label={t("EMPLOYEEOVERVIEW_SEARCH_ARIA", undefined, "Search employees by first or last name")}
                        rightSection={
                            inputValue && (
                                <ActionIcon
                                    onClick={handleClear}
                                    variant="subtle"
                                    color="gray"
                                    size="sm"
                                    aria-label={t("EMPLOYEEOVERVIEW_SEARCH_CLEAR_ARIA", undefined, "Clear search")}
                                >
                                    <IconXMark width={16} />
                                </ActionIcon>
                            )
                        }
                    />
                </Box>
            </Group>

            {/* ── Row 2: filter pills ── */}
            <Group gap="xs" align="flex-start" wrap="wrap">
                <FilterPill
                    icon={<IconBuilding style={{ width: rem(14), height: rem(14), color: pillIconColor(filters.costCenterIds.length > 0) }} />}
                    label={t("EMPLOYEEOVERVIEW_FILTER_COST_CENTER", undefined, "Cost Center")}
                    tags={filters.costCenterIds.map((id) => ({
                        label: toOptions(dictionaries.costCenters.data).find((o) => o.value === id.toString())?.label ?? String(id),
                        onRemove: () => filters.setCostCenterIds(filters.costCenterIds.filter((existing) => existing !== id)),
                    }))}
                >
                    <Box pt={6}>
                        <MultiSelect
                            placeholder={t("EMPLOYEEOVERVIEW_FILTER_COST_CENTER_PLACEHOLDER", undefined, "Any cost center")}
                            value={idsToStrings(filters.costCenterIds)}
                            onChange={(values) => {
                                filters.setCostCenterIds(stringsToIds(values));
                                filters.setPageIndex(0);
                            }}
                            data={toOptions(dictionaries.costCenters.data)}
                            size="xs"
                            searchable
                            clearable
                            style={{ minWidth: rem(200) }}
                        />
                    </Box>
                </FilterPill>

                <FilterPill
                    icon={<IconBriefcase style={{ width: rem(14), height: rem(14), color: pillIconColor(filters.projectIds.length > 0) }} />}
                    label={t("EMPLOYEEOVERVIEW_FILTER_PROJECT", undefined, "Project")}
                    tags={filters.projectIds.map((id) => ({
                        label: toOptions(dictionaries.projects.data).find((o) => o.value === id.toString())?.label ?? String(id),
                        onRemove: () => filters.setProjectIds(filters.projectIds.filter((existing) => existing !== id)),
                    }))}
                >
                    <Box pt={6}>
                        <MultiSelect
                            placeholder={t("EMPLOYEEOVERVIEW_FILTER_PROJECT_PLACEHOLDER", undefined, "Any project")}
                            value={idsToStrings(filters.projectIds)}
                            onChange={(values) => {
                                filters.setProjectIds(stringsToIds(values));
                                filters.setPageIndex(0);
                            }}
                            data={toOptions(dictionaries.projects.data)}
                            size="xs"
                            searchable
                            clearable
                            style={{ minWidth: rem(200) }}
                        />
                    </Box>
                </FilterPill>

                <FilterPill
                    icon={<IconClock style={{ width: rem(14), height: rem(14), color: pillIconColor(filters.workScheduleIds.length > 0) }} />}
                    label={t("EMPLOYEEOVERVIEW_FILTER_WORK_SCHEDULE", undefined, "Work Schedule")}
                    tags={filters.workScheduleIds.map((id) => ({
                        label: toOptions(dictionaries.workSchedules.data).find((o) => o.value === id.toString())?.label ?? String(id),
                        onRemove: () => filters.setWorkScheduleIds(filters.workScheduleIds.filter((existing) => existing !== id)),
                    }))}
                >
                    <Box pt={6}>
                        <MultiSelect
                            placeholder={t("EMPLOYEEOVERVIEW_FILTER_WORK_SCHEDULE_PLACEHOLDER", undefined, "Any work schedule")}
                            value={idsToStrings(filters.workScheduleIds)}
                            onChange={(values) => {
                                filters.setWorkScheduleIds(stringsToIds(values));
                                filters.setPageIndex(0);
                            }}
                            data={toOptions(dictionaries.workSchedules.data)}
                            size="xs"
                            searchable
                            clearable
                            style={{ minWidth: rem(200) }}
                        />
                    </Box>
                </FilterPill>

                <FilterPill
                    icon={<IconIdentification style={{ width: rem(14), height: rem(14), color: pillIconColor(filters.contractTypeIds.length > 0) }} />}
                    label={t("EMPLOYEEOVERVIEW_FILTER_EMPLOYMENT_TYPE", undefined, "Employment Type")}
                    tags={filters.contractTypeIds.map((id) => ({
                        label: toOptions(dictionaries.contractTypes.data).find((o) => o.value === id.toString())?.label ?? String(id),
                        onRemove: () => filters.setContractTypeIds(filters.contractTypeIds.filter((existing) => existing !== id)),
                    }))}
                >
                    <Box pt={6}>
                        <MultiSelect
                            placeholder={t("EMPLOYEEOVERVIEW_FILTER_EMPLOYMENT_TYPE_PLACEHOLDER", undefined, "Any employment type")}
                            value={idsToStrings(filters.contractTypeIds)}
                            onChange={(values) => {
                                filters.setContractTypeIds(stringsToIds(values));
                                filters.setPageIndex(0);
                            }}
                            data={toOptions(dictionaries.contractTypes.data)}
                            size="xs"
                            searchable
                            clearable
                            style={{ minWidth: rem(200) }}
                        />
                    </Box>
                </FilterPill>

                <FilterPill
                    icon={<IconUserGroup style={{ width: rem(14), height: rem(14), color: pillIconColor(filters.positionIds.length > 0 || filters.includeNoPosition) }} />}
                    label={t("EMPLOYEEOVERVIEW_FILTER_POSITION", undefined, "Position")}
                    tags={[
                        ...filters.positionIds.map((id) => ({
                            label: toOptions(dictionaries.positions.data).find((o) => o.value === id.toString())?.label ?? String(id),
                            onRemove: () => filters.setPositionIds(filters.positionIds.filter((existing) => existing !== id)),
                        })),
                        ...(filters.includeNoPosition
                            ? [{ label: t("EMPLOYEEOVERVIEW_FILTER_NO_POSITION", undefined, "No position assigned"), onRemove: () => filters.setIncludeNoPosition(false) }]
                            : []),
                    ]}
                >
                    <Stack gap={6} pt={6}>
                        <MultiSelect
                            placeholder={t("EMPLOYEEOVERVIEW_FILTER_POSITION_PLACEHOLDER", undefined, "Any position")}
                            value={idsToStrings(filters.positionIds)}
                            onChange={(values) => {
                                filters.setPositionIds(stringsToIds(values));
                                filters.setPageIndex(0);
                            }}
                            data={toOptions(dictionaries.positions.data)}
                            size="xs"
                            searchable
                            clearable
                            style={{ minWidth: rem(200) }}
                        />
                        <Switch
                            label={t("EMPLOYEEOVERVIEW_FILTER_NO_POSITION", undefined, "No position assigned")}
                            size="xs"
                            checked={filters.includeNoPosition}
                            onChange={(e) => {
                                filters.setIncludeNoPosition(e.currentTarget.checked);
                                filters.setPageIndex(0);
                            }}
                        />
                    </Stack>
                </FilterPill>

                <FilterPill
                    icon={<IconCalendarDays style={{ width: rem(14), height: rem(14), color: pillIconColor(!!filters.workStartFrom || !!filters.workStartTo || !!filters.workEndFrom || !!filters.workEndTo) }} />}
                    label={t("EMPLOYEEOVERVIEW_FILTER_WORK_DATES", undefined, "Work Dates")}
                    tags={[
                        ...(filters.workStartFrom || filters.workStartTo ? [{ label: t("EMPLOYEEOVERVIEW_FILTER_WORK_START_RANGE_TAG", undefined, "Work start range"), onRemove: () => { filters.setWorkStartFrom(null); filters.setWorkStartTo(null); } }] : []),
                        ...(filters.workEndFrom || filters.workEndTo ? [{ label: t("EMPLOYEEOVERVIEW_FILTER_WORK_END_RANGE_TAG", undefined, "Work end range"), onRemove: () => { filters.setWorkEndFrom(null); filters.setWorkEndTo(null); } }] : []),
                    ]}
                >
                    <Stack gap={6} pt={6} style={{ minWidth: rem(180) }}>
                        <Text size="xs" c="dimmed">{t("EMPLOYEEOVERVIEW_FILTER_WORK_START_BETWEEN", undefined, "Work start between")}</Text>
                        <Group gap={4} wrap="nowrap">
                            <DateInput
                                placeholder={t("EMPLOYEEOVERVIEW_FILTER_DATE_FROM", undefined, "From")}
                                size="xs"
                                value={fromIsoDate(filters.workStartFrom)}
                                onChange={(date) => { filters.setWorkStartFrom(toIsoDate(date) ?? null); filters.setPageIndex(0); }}
                                styles={dateInputStyles}
                                {...dateInputProps}
                            />
                            <DateInput
                                placeholder={t("EMPLOYEEOVERVIEW_FILTER_DATE_TO", undefined, "To")}
                                size="xs"
                                value={fromIsoDate(filters.workStartTo)}
                                onChange={(date) => { filters.setWorkStartTo(toIsoDate(date) ?? null); filters.setPageIndex(0); }}
                                styles={dateInputStyles}
                                {...dateInputProps}
                            />
                        </Group>
                        <Text size="xs" c="dimmed" mt={4}>{t("EMPLOYEEOVERVIEW_FILTER_WORK_END_BETWEEN", undefined, "Work end between")}</Text>
                        <Group gap={4} wrap="nowrap">
                            <DateInput
                                placeholder={t("EMPLOYEEOVERVIEW_FILTER_DATE_FROM", undefined, "From")}
                                size="xs"
                                value={fromIsoDate(filters.workEndFrom)}
                                onChange={(date) => { filters.setWorkEndFrom(toIsoDate(date) ?? null); filters.setPageIndex(0); }}
                                styles={dateInputStyles}
                                {...dateInputProps}
                            />
                            <DateInput
                                placeholder={t("EMPLOYEEOVERVIEW_FILTER_DATE_TO", undefined, "To")}
                                size="xs"
                                value={fromIsoDate(filters.workEndTo)}
                                onChange={(date) => { filters.setWorkEndTo(toIsoDate(date) ?? null); filters.setPageIndex(0); }}
                                styles={dateInputStyles}
                                {...dateInputProps}
                            />
                        </Group>
                    </Stack>
                </FilterPill>

                <FilterPill
                    icon={<IconClock style={{ width: rem(14), height: rem(14), color: pillIconColor(!!filters.availabilityFrom || !!filters.availabilityTo) }} />}
                    label={t("EMPLOYEEOVERVIEW_AVAILABILITY_LABEL", undefined, "Availability")}
                    tags={
                        filters.availabilityFrom || filters.availabilityTo
                            ? [{ label: t("EMPLOYEEOVERVIEW_FILTER_AVAILABILITY_RANGE_TAG", undefined, "Availability range"), onRemove: () => { filters.setAvailabilityFrom(null); filters.setAvailabilityTo(null); } }]
                            : []
                    }
                >
                    <Stack gap={6} pt={6} style={{ minWidth: rem(180) }}>
                        <Group gap={4} wrap="nowrap">
                            <DateInput
                                placeholder={t("EMPLOYEEOVERVIEW_FILTER_DATE_FROM", undefined, "From")}
                                size="xs"
                                value={fromIsoDate(filters.availabilityFrom)}
                                onChange={(date) => { filters.setAvailabilityFrom(toIsoDate(date) ?? null); filters.setPageIndex(0); }}
                                styles={dateInputStyles}
                                {...dateInputProps}
                            />
                            <DateInput
                                placeholder={t("EMPLOYEEOVERVIEW_FILTER_DATE_TO", undefined, "To")}
                                size="xs"
                                value={fromIsoDate(filters.availabilityTo)}
                                onChange={(date) => { filters.setAvailabilityTo(toIsoDate(date) ?? null); filters.setPageIndex(0); }}
                                styles={dateInputStyles}
                                {...dateInputProps}
                            />
                        </Group>
                    </Stack>
                </FilterPill>

                <FilterPill
                    icon={<IconDocumentText style={{ width: rem(14), height: rem(14), color: pillIconColor(filters.noPersonId) }} />}
                    label={t("EMPLOYEEOVERVIEW_FILTER_NO_CV_YET", undefined, "No CV Yet")}
                    tags={filters.noPersonId ? [{ label: t("EMPLOYEEOVERVIEW_FILTER_NO_CV_YET_TAG", undefined, "No CV yet"), onRemove: () => filters.setNoPersonId(false) }] : []}
                >
                    <Box pt={6}>
                        <Tooltip label={t("EMPLOYEEOVERVIEW_FILTER_NO_CV_TOOLTIP", undefined, "Show only employees with no linked CV — useful before a bulk reminder")} withArrow position="bottom">
                            <Switch
                                label={t("EMPLOYEEOVERVIEW_FILTER_NO_CV_YET_TAG", undefined, "No CV yet")}
                                size="xs"
                                checked={filters.noPersonId}
                                onChange={(e) => {
                                    filters.setNoPersonId(e.currentTarget.checked);
                                    filters.setPageIndex(0);
                                }}
                            />
                        </Tooltip>
                    </Box>
                </FilterPill>

                <FilterPill
                    icon={
                        <IconUser
                            style={{
                                width: rem(14),
                                height: rem(14),
                                color: pillIconColor(filters.includeInactive),
                            }}
                        />
                    }
                    label={t("EMPLOYEEOVERVIEW_FILTER_SHOW_INACTIVE", undefined, "Show Inactive")}
                    tags={
                        filters.includeInactive
                            ? [{ label: t("EMPLOYEEOVERVIEW_INACTIVE_BADGE", undefined, "Inactive"), onRemove: () => { filters.setIncludeInactive(false); filters.setPageIndex(0); } }]
                            : []
                    }
                >
                    <Box pt={6}>
                        <Button
                            fullWidth
                            size="compact-xs"
                            variant={filters.includeInactive ? "filled" : "light"}
                            color="brand"
                            onClick={() => {
                                filters.setIncludeInactive(!filters.includeInactive);
                                filters.setPageIndex(0);
                            }}
                            aria-pressed={filters.includeInactive}
                        >
                            {filters.includeInactive
                                ? t("EMPLOYEEOVERVIEW_INCLUDING_INACTIVE_BUTTON", undefined, "✓ Including inactive")
                                : t("EMPLOYEEOVERVIEW_INCLUDE_INACTIVE_BUTTON", undefined, "Include inactive")}
                        </Button>
                    </Box>
                </FilterPill>
            </Group>
        </Stack>
    );
}
